// @ts-nocheck
import { useEffect, useState } from 'react';

import { Text, XStack, YStack } from 'tamagui';

import { Header, Footer, SubscribeForm } from '../../components/landing';

const COMPARISON_ROWS = [
  { feature: 'Target audience', torch: 'Individuals, small teams, and enterprises', glean: 'Enterprises (500+ employees)' },
  { feature: 'Pricing', torch: 'Free / $14 Pro / $22 Team per user/mo', glean: 'Enterprise pricing (typically $10+/user/mo with minimums)' },
  { feature: 'Minimum seats', torch: '1 (free tier)', glean: 'Typically 500+' },
  { feature: 'Cross-ecosystem search', torch: true, glean: true },
  { feature: 'Microsoft + Google + Slack', torch: true, glean: true },
  { feature: 'Kanban boards', torch: true, glean: false },
  { feature: 'Calendar', torch: true, glean: false },
  { feature: 'Documents', torch: true, glean: false },
  { feature: 'Wiki', torch: true, glean: false },
  { feature: 'OKR tracking', torch: true, glean: false },
  { feature: 'Sprint management', torch: true, glean: false },
  { feature: 'Announcements', torch: true, glean: false },
  { feature: 'AI assistant', torch: 'Roadmap', glean: true },
  { feature: 'Self-serve signup', torch: true, glean: false },
  { feature: 'SSO / SAML', torch: 'Team plan', glean: true },
];

export default function TorchVsGlean() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderCell = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return (
        <Text fontSize={14} color={value ? '#ededed' : '$color6'}>
          {value ? '\u2713' : '\u2014'}
        </Text>
      );
    }
    return (
      <Text fontSize={14} color="$color11" lineHeight={20}>
        {value}
      </Text>
    );
  };

  return (
    <YStack backgroundColor="$background" minHeight="100vh">
      <Header />

      <YStack paddingTop={140} paddingHorizontal="$5" gap="$8" paddingBottom={80}>
        <YStack maxWidth={900} alignSelf="center" width="100%">
          {/* Page header */}
          <YStack alignItems="center" marginBottom={60}>
            <Text
              fontSize={13}
              fontWeight="600"
              color="#ededed"
              letterSpacing={2}
              textTransform="uppercase"
              marginBottom="$3"
            >
              Comparison
            </Text>
            <Text
              fontSize={isMobile ? 30 : 44}
              fontWeight="700"
              textAlign="center"
              letterSpacing={-1.5}
              lineHeight={isMobile ? 36 : 52}
              color="$color12"
            >
              Torch vs Glean
            </Text>
            <Text
              fontSize={isMobile ? 16 : 18}
              color="$color9"
              textAlign="center"
              marginTop="$3"
              lineHeight={28}
              maxWidth={600}
            >
              Glean is enterprise search for large organizations. Torch brings the same cross-ecosystem
              search to individuals and small teams, with built-in productivity apps included.
            </Text>
          </YStack>

          {/* Comparison table */}
          <YStack
            borderRadius="$3"
            borderWidth={1}
            borderColor="$color4"
            overflow="hidden"
            marginBottom={60}
          >
            {/* Table header */}
            <XStack
              backgroundColor="$color2"
              paddingVertical="$3"
              paddingHorizontal="$4"
              borderBottomWidth={1}
              borderColor="$color4"
            >
              <div style={{ flex: 2 }}>
                <Text fontSize={13} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={1}>
                  Feature
                </Text>
              </div>
              <div style={{ flex: 2 }}>
                <Text fontSize={13} fontWeight="600" color="#ededed" textTransform="uppercase" letterSpacing={1}>
                  Torch
                </Text>
              </div>
              <div style={{ flex: 2 }}>
                <Text fontSize={13} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={1}>
                  Glean
                </Text>
              </div>
            </XStack>

            {COMPARISON_ROWS.map((row, i) => (
              <XStack
                key={row.feature}
                paddingVertical="$3"
                paddingHorizontal="$4"
                borderBottomWidth={i < COMPARISON_ROWS.length - 1 ? 1 : 0}
                borderColor="$color3"
                hoverStyle={{ backgroundColor: '$color2' }}
                flexDirection={isMobile ? 'column' : 'row'}
                gap={isMobile ? '$2' : undefined}
              >
                <div style={{ flex: 2 }}>
                  <Text fontSize={14} color="$color11" fontWeight="500">
                    {row.feature}
                  </Text>
                </div>
                <div style={{ flex: 2 }}>{renderCell(row.torch)}</div>
                <div style={{ flex: 2 }}>{renderCell(row.glean)}</div>
              </XStack>
            ))}
          </YStack>

          {/* When to choose sections */}
          <XStack
            gap="$5"
            flexDirection={isMobile ? 'column' : 'row'}
            marginBottom={60}
          >
            <YStack
              flex={1}
              padding="$5"
              borderRadius="$3"
              borderWidth={1}
              borderColor="$color4"
              gap="$3"
            >
              <Text fontSize={20} fontWeight="600" color="$color12">
                When to choose Glean
              </Text>
              <YStack gap="$2">
                {[
                  'Your company has 500+ employees.',
                  'You have an enterprise budget for search tooling.',
                  'You need AI assistant features for enterprise knowledge.',
                  'You need deep enterprise admin controls and compliance features.',
                ].map((point, i) => (
                  <XStack key={i} gap="$2" alignItems="flex-start">
                    <Text fontSize={14} color="$color7" style={{ marginTop: 2 } as any}>
                      {'\u2022'}
                    </Text>
                    <Text fontSize={14} color="$color10" lineHeight={22} flex={1}>
                      {point}
                    </Text>
                  </XStack>
                ))}
              </YStack>
            </YStack>

            <YStack
              flex={1}
              padding="$5"
              borderRadius="$3"
              borderWidth={2}
              borderColor="$color6"
              gap="$3"
              style={{ boxShadow: '0 0 30px rgba(255, 255, 255, 0.1)' }}
            >
              <Text fontSize={20} fontWeight="600" color="$color12">
                When to choose Torch
              </Text>
              <YStack gap="$2">
                {[
                  'You\'re an individual or small team that needs cross-app search.',
                  'You want built-in Kanban, Calendar, Docs, and Wiki — not just search.',
                  'You want to start free and scale up as you grow.',
                  'You want to replace multiple SaaS subscriptions with one workspace.',
                ].map((point, i) => (
                  <XStack key={i} gap="$2" alignItems="flex-start">
                    <Text fontSize={14} color="$color7" style={{ marginTop: 2 } as any}>
                      {'\u2022'}
                    </Text>
                    <Text fontSize={14} color="$color10" lineHeight={22} flex={1}>
                      {point}
                    </Text>
                  </XStack>
                ))}
              </YStack>
            </YStack>
          </XStack>

          {/* CTA */}
          <YStack alignItems="center" gap="$4">
            <Text
              fontSize={isMobile ? 22 : 28}
              fontWeight="700"
              textAlign="center"
              letterSpacing={-0.5}
              color="$color12"
            >
              Ready to try Torch?
            </Text>
            <Text fontSize={16} color="$color9" textAlign="center" marginBottom="$2">
              Join the waitlist and be the first to know when we launch.
            </Text>
            <SubscribeForm />
          </YStack>
        </YStack>
      </YStack>

      <Footer />
    </YStack>
  );
}
